import cn from 'classnames'
import {Fragment, useId} from 'react'
import type {PresetKey} from 'slices/RoiCalculator/types'

export type PresetOption = {
  key: PresetKey
  label: string
}

export type PresetPickerProps = {
  label: string
  options: PresetOption[]
  selected: PresetKey | null
  onSelect: (key: PresetKey) => void
}

export const PresetPicker = ({label, options, selected, onSelect}: PresetPickerProps) => {
  const groupName = useId()

  return (
    <fieldset className="flex flex-col gap-3.5">
      <legend className="pb-3.5 text-16 leading-none text-moss-grey-550">{label}</legend>
      <div className="flex items-center rounded-lg border border-moss-black/10 bg-moss-white">
        {options.map((option, index) => {
          const prevSelected = index > 0 && options[index - 1].key === selected
          const currentSelected = option.key === selected
          const showDivider = index > 0 && !prevSelected && !currentSelected

          return (
            <Fragment key={option.key}>
              {index > 0 && (
                <div
                  className={cn('h-6 w-px transition-opacity', showDivider ? 'bg-moss-black/10' : 'bg-transparent')}
                />
              )}
              <label
                className={cn(
                  'flex flex-1 cursor-pointer items-center justify-center px-2 py-3.5 text-16 first:rounded-l-lg last:rounded-r-lg',
                  'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-green-dark',
                  currentSelected
                    ? 'bg-moss-blue-light font-medium text-moss-black'
                    : 'text-moss-grey-550 hover:bg-moss-blue-light/40',
                )}
              >
                <input
                  type="radio"
                  name={groupName}
                  value={option.key}
                  checked={currentSelected}
                  onChange={() => onSelect(option.key)}
                  className="sr-only"
                />
                {option.label}
              </label>
            </Fragment>
          )
        })}
      </div>
    </fieldset>
  )
}
